import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FacebookIcon } from "../../social/Facebook/FacebookIcon";
import { InstagramIcon } from "../../social/Instagram/InstagramIcon";
import { LinkedInIcon } from "../../social/LinkedIn/LinkedInIcon";
import { LinkedInBusinessIcon } from "../../social/LinkedIn/LinkedInBusinessIcon";
import { TiktokIcon } from "../../social/Tiktok/TiktokIcon";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import FacebookAuthButton from "../../social/Facebook/FacebookAuthButton";
import InstagramAuthButton from "../../social/Instagram/InstagramBindCard";
import LinkedInAuthButton from "../../social/LinkedIn/LinkedInBindCard";
import LinkedInBusinessAuthButton from "../../social/LinkedIn/LinkedInBusinessBindCard";
import TiktokAuthButton from "../../social/Tiktok/TiktokBindCard";
import "./ConnectSocialModal.scss";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onConnect: (providerId: string) => void;
}

export const ConnectSocialModal = ({ isOpen, toggle, onConnect }: Props) => {
  const [selectedProvider, setSelectedProvider] = useState<string>("");

  const availableProviders = [
    {
      id: SocialProviderId.Facebook,
      name: "Facebook",
      icon: "facebook",
      color: "#1877F2",
      description: "Conecta tu cuenta de Facebook para publicar posts",
      iconComponent: <FacebookIcon size="2x" color="#1877F2" />,
      authComponent: <FacebookAuthButton />
    },
    {
      id: SocialProviderId.Instagram,
      name: "Instagram",
      icon: "instagram", 
      color: "#E4405F",
      description: "Conecta tu cuenta de Instagram para publicar posts",
      iconComponent: <InstagramIcon size="2x" color="#E4405F" />,
      authComponent: <InstagramAuthButton />
    },
    {
      id: SocialProviderId.LinkedIn,
      name: "LinkedIn",
      icon: "linkedIn",
      color: "#0077B5",
      description: "Conecta tu cuenta personal de LinkedIn",
      iconComponent: <LinkedInIcon size="2x" color="#0077B5" />,
      authComponent: <LinkedInAuthButton />
    },
    {
      id: SocialProviderId.LinkedInBusiness,
      name: "LinkedIn Business",
      icon: "linkedInBusiness",
      color: "#0077B5",
      description: "Conecta tu cuenta de LinkedIn Business para publicar posts",
      iconComponent: <LinkedInBusinessIcon size="2x" color="#0077B5" />,
      authComponent: <LinkedInBusinessAuthButton />
    },
    {
      id: SocialProviderId.TikTok,
      name: "TikTok",
      icon: "tiktok",
      color: "#000000",
      description: "Conecta tu cuenta de TikTok para publicar videos",
      iconComponent: <TiktokIcon size="2x" color="#000000" />,
      authComponent: <TiktokAuthButton />
    }
  ];

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
  };

  const handleConnect = () => {
    if (selectedProvider) {
      onConnect(selectedProvider);
      setSelectedProvider("");
      toggle();
    }
  };

  const handleClose = () => {
    setSelectedProvider("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" className="connect-social-modal">
      <ModalHeader toggle={handleClose}>
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          <span>Conectar Redes Sociales</span>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="providers-grid">
          {availableProviders.map((provider) => (
            <div
              key={provider.id}
              className={`provider-card ${selectedProvider === provider.id ? 'selected' : ''}`}
              onClick={() => handleProviderSelect(provider.id)}
            >
              <div className="provider-icon">
                {provider.iconComponent}
              </div>
              <div className="provider-info">
                <h6 className="provider-name">{provider.name}</h6>
                <p className="provider-description">{provider.description}</p>
              </div>
              {selectedProvider === provider.id && (
                <div className="selected-indicator">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {selectedProvider && (
          <div className="mt-4">
            <div className="auth-section">
              <h6 className="auth-title">Conectar {availableProviders.find(p => p.id === selectedProvider)?.name}</h6>
              <div className="auth-button-container">
                {availableProviders.find(p => p.id === selectedProvider)?.authComponent}
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleConnect}
          disabled={!selectedProvider}
        >
          Conectar
        </Button>
      </ModalFooter>
    </Modal>
  );
};
